import type { IpttqRecord, IpttqSkillSpec, IpttqSuggestNewResult } from '@kelilearning/psycheflow-contracts';

import { apiClient } from './apiClient';
import { notifyHomeTtqBurdenStatsInvalidated } from './homeTtqBurdenInvalidation';
import {
  invalidateIpttqRecord,
  peekIpttqRecord,
  rememberIpttqRecord,
} from './ipttqRecordCache';
import { invalidateTtqSessionList } from './ttqSessionListCache';

export type IpttqListPayload = {
  records: IpttqRecord[];
  hasBankWarning: boolean;
  hasIskWarning?: boolean;
};

export type IpttqRefreshScope = 'skill' | 'bank' | 'all';

export type IpttqRefreshPayload = {
  record: IpttqRecord;
  bankAppended: number;
  needsBankWarning: boolean;
  needsIskWarning?: boolean;
  scope?: IpttqRefreshScope;
  similarityMerges?: Array<{
    survivorId: string;
    absorbedId: string;
    reason: string;
  }>;
};

export type IpttqBankStepPayload = {
  record: IpttqRecord;
  appended: 0 | 1;
  bankCount: number;
  targetBank: number;
  done: boolean;
  skipReason?: string;
  needsBankWarning?: boolean;
  needsIskWarning?: boolean;
};

export type IpttqGenerateBankPayload = {
  record: IpttqRecord;
  started: boolean;
  alreadyRunning: boolean;
  bankGeneration: NonNullable<IpttqRecord['bankGeneration']>;
  needsBankWarning?: boolean;
  needsIskWarning?: boolean;
};

export type IpttqIskRefreshPayload = {
  record: IpttqRecord;
  needsIskWarning: boolean;
  needsBankWarning: boolean;
  started?: boolean;
  alreadyRunning?: boolean;
};

export type IpttqImageGenerationProviders = {
  defaultProvider: string;
  defaultModel: string;
  providers: Array<{
    id: string;
    label: string;
    models: Array<{ id: string; label: string; provider?: string; unavailable?: boolean }>;
  }>;
};

export type IpttqCreateOptions = {
  skill?: Partial<IpttqSkillSpec> | null;
  bankMode?: 'session_originals_ref';
};

function normalizeCreateOptions(
  skillOrOptions?: Partial<IpttqSkillSpec> | null | IpttqCreateOptions,
): IpttqCreateOptions {
  if (skillOrOptions && typeof skillOrOptions === 'object' && 'bankMode' in skillOrOptions) {
    return skillOrOptions;
  }
  return { skill: (skillOrOptions ?? null) as Partial<IpttqSkillSpec> | null };
}

export const ipttqService = {
  async listByTtqa(ttqaId: string): Promise<IpttqListPayload> {
    return apiClient.getEnvelope<IpttqListPayload>(`/api/ttqa/${ttqaId}/ipttq`);
  },

  async get(ipttqId: string, options?: { forceRefresh?: boolean }): Promise<IpttqRecord> {
    const id = String(ipttqId || '').trim();
    if (!options?.forceRefresh) {
      const cached = peekIpttqRecord(id);
      if (cached) return cached;
    }
    const record = await apiClient.getEnvelope<IpttqRecord>(`/api/ipttq/${id}`);
    rememberIpttqRecord(record);
    return record;
  },

  async refresh(
    ipttqId: string,
    options?: { scope?: IpttqRefreshScope },
  ): Promise<IpttqRefreshPayload> {
    const data = await apiClient.postEnvelope<IpttqRefreshPayload>(
      `/api/ipttq/${ipttqId}/refresh`,
      { scope: options?.scope || 'all' },
      true,
      { timeoutMs: 180_000 },
    );
    rememberIpttqRecord(data.record);
    invalidateTtqSessionList();
    notifyHomeTtqBurdenStatsInvalidated();
    return data;
  },

  /** 后台生成诱导题库（立刻返回；离开页面不影响） */
  async generateBank(
    ipttqId: string,
    options?: { count?: number },
  ): Promise<IpttqGenerateBankPayload> {
    const data = await apiClient.postEnvelope<IpttqGenerateBankPayload>(
      `/api/ipttq/${ipttqId}/generate-bank`,
      { count: options?.count },
      true,
      { timeoutMs: 30_000 },
    );
    rememberIpttqRecord(data.record);
    invalidateTtqSessionList();
    notifyHomeTtqBurdenStatsInvalidated();
    return data;
  },

  /** @deprecated 逐步生成；优先 generateBank 后台任务 */
  async refreshBankStep(ipttqId: string): Promise<IpttqBankStepPayload> {
    const data = await apiClient.postEnvelope<IpttqBankStepPayload>(
      `/api/ipttq/${ipttqId}/refresh-bank-step`,
      {},
      true,
      { timeoutMs: 120_000 },
    );
    if (data.appended > 0 || data.done) {
      notifyHomeTtqBurdenStatsInvalidated();
    }
    return data;
  },

  /** 单独更新直觉草图；可选换生图模型（异步：立刻返回 pending，需轮询 get） */
  async refreshIsk(
    ipttqId: string,
    options?: { model?: string; provider?: 'dashscope' | 'banana' | 'image2' | 'wavespeed' },
  ): Promise<IpttqIskRefreshPayload> {
    return apiClient.postEnvelope<IpttqIskRefreshPayload>(
      `/api/ipttq/${ipttqId}/refresh-isk`,
      {
        imageGeneration: {
          enabled: true,
          provider: options?.provider || 'image2',
          model: options?.model,
        },
      },
      true,
      { timeoutMs: 30_000 },
    );
  },

  /** 上传手绘直觉草图（拍照/相册） */
  async uploadIsk(ipttqId: string, file: File): Promise<IpttqIskRefreshPayload> {
    const formData = new FormData();
    formData.append('file', file, file.name || 'hand-drawn.jpg');
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: IpttqIskRefreshPayload;
      error?: string;
    }>(`/api/ipttq/${encodeURIComponent(ipttqId)}/upload-isk`, {
      method: 'POST',
      body: formData,
      auth: true,
      timeoutMs: 60_000,
    });
    if (!payload.success || !payload.data) {
      throw new Error(payload.error || '上传手绘草图失败');
    }
    return payload.data;
  },

  /** 手绘修图（与原始生图共用每日额度） */
  async repairIsk(
    ipttqId: string,
    options?: { model?: string; provider?: 'dashscope' | 'banana' | 'image2' | 'wavespeed' },
  ): Promise<IpttqIskRefreshPayload> {
    return apiClient.postEnvelope<IpttqIskRefreshPayload>(
      `/api/ipttq/${ipttqId}/repair-isk`,
      {
        imageGeneration: {
          enabled: true,
          provider: options?.provider || 'image2',
          model: options?.model,
        },
      },
      true,
      { timeoutMs: 30_000 },
    );
  },

  /** 勾选直觉草图展示槽 */
  async selectIskSlot(
    ipttqId: string,
    slot: 'original' | 'handDrawn' | 'repaired',
  ): Promise<IpttqIskRefreshPayload> {
    return apiClient.postEnvelope<IpttqIskRefreshPayload>(
      `/api/ipttq/${ipttqId}/select-isk-slot`,
      { slot },
      true,
    );
  },

  async listIskImageGenerationProviders(): Promise<IpttqImageGenerationProviders> {
    return apiClient.getEnvelope<IpttqImageGenerationProviders>(
      '/api/ipttq/image-generation-providers',
    );
  },

  async suggestNew(ttqaId: string): Promise<IpttqSuggestNewResult> {
    return apiClient.postEnvelope<IpttqSuggestNewResult>(
      `/api/ttqa/${ttqaId}/ipttq/suggest-new`,
      {},
      true,
      { timeoutMs: 120_000 },
    );
  },

  async create(
    ttqaId: string,
    skillOrOptions?: Partial<IpttqSkillSpec> | null | IpttqCreateOptions,
  ): Promise<IpttqRecord> {
    const options = normalizeCreateOptions(skillOrOptions);
    const data = await apiClient.postEnvelope<IpttqRecord>(`/api/ttqa/${ttqaId}/ipttq`, {
      skill: options.skill || null,
      bankMode: options.bankMode,
    });
    notifyHomeTtqBurdenStatsInvalidated();
    return data;
  },

  async createPlainOriginalRef(ttqaId: string): Promise<IpttqRecord> {
    return this.create(ttqaId, { bankMode: 'session_originals_ref' });
  },

  async archive(ipttqId: string): Promise<IpttqRecord> {
    const data = await apiClient.patchEnvelope<IpttqRecord>(`/api/ipttq/${ipttqId}`, { lifecycle: 'archived' });
    notifyHomeTtqBurdenStatsInvalidated();
    return data;
  },
};
